import { enqueueSnackbar } from 'notistack';
import { directLogout, selectAuthToken } from '../features/auth/authSlice';
import { TOAST_ERROR } from '../utils/constants';
import i18next from 'i18next';
import { authApi } from '../utils/urls';

/**
 * authStatusCheck was not being called after a 401 error, so:
 * Direct call to the auth API to check session status
 * This bypasses the Redux flow and avoids circular dependency issues
 */
const directAuthStatusCheck = () => {
  const token = localStorage.getItem('token');  
  return fetch(authApi(), {
    method: 'POST',
    headers: { 
      token: token || '',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ method: 'status' }),
  })
  .then(res => {
    return res.json();
  })
  .catch(err => {
    return { error: err.message };
  });
};

const handleUnauthorized = (dispatch, url, body) => {
  // Prevent circular calls by checking if we're already in an authStatus call
  if (url === authApi() && body?.method === 'status') {
    // If we get a 401 during an authStatus call itself, then the session is truly expired
    dispatch(directLogout());
    return;
  }
  // Important: Add a delay to break out of the current execution context
  // This helps avoid potential Redux dispatch issues and circular dependencies
  setTimeout(() => {
    directAuthStatusCheck()
      .then(data => {
        if (data && data.user) {
          // User is still authenticated, update Redux store manually
          dispatch({ 
            type: 'login/authStatus/fulfilled', 
            payload: data,
            meta: { arg: undefined, requestId: 'manual-' + Date.now(), requestStatus: 'fulfilled' } 
          });
        } else {
          // No user returned, logout
          dispatch(directLogout());
        }
      })
      .catch(err => {
        // If auth status check failed, logout
        dispatch(directLogout());
      });
  }, 0);
};

const httpClient = {
  post: (url, body, { getState, dispatch, rejectWithValue, noRedirect = false, responseType = 'json', noToken = false, signal }) => {
    const t = i18next.t;
    const authToken = selectAuthToken(getState());

    if (!url) {
      return enqueueSnackbar(t('httpClient.serverError'), { variant: TOAST_ERROR });
    }

    if (!noToken && !authToken) {
      // console.log('invalidToken');
      return;
    } else {
      const init = {
        method: 'POST',
        headers: { token: authToken },
        body: body instanceof FormData ? body : JSON.stringify(body),
        signal: signal, // Pass the signal to the fetch call
      };

      return fetch(url, init)
        .then(async res => {
          if (!res.ok) {
            let errorResponse;
            
            try {
              errorResponse = await res.json();
            } catch (e) {
              errorResponse = { errMsg: 'Failed to parse response' };
            }
                      
            if (res.status === 500) {
              !noRedirect && enqueueSnackbar(t('httpClient.serverError'), { variant: TOAST_ERROR });
              return { data: rejectWithValue({ errorCode: res.status, response: errorResponse }) };
            } else if (res.status < 200 || res.status >= 300) {
              if (res.status === 401) {
                // console.error('401 Unauthorized response detected - will trigger auth check');
                handleUnauthorized(dispatch, url, body);
              }
              
              return { data: rejectWithValue({ errorCode: res.status, response: errorResponse }) };
            } else {
              console.error(res);
              throw Error(t('httpClient.fetchError', { url }));
            }
          }

          if (responseType === 'json') {
            return res.json().then(json => {
              if (json.reason && json.time) {
                throw Error(json.reason);
              }
              return json;
            });
          } else {
            return res;
          }
        })
        .catch(error => {
          if (error.name === 'AbortError') {
            // console.log(`Fetch aborted for URL: ${url}`, error);
          }
          throw error; // Re-throw error to ensure promise remains rejected
        });
    }
  }
};

export default httpClient;
